      // src/scenes/dashboard/dashboard.jsx
      import React, { useState } from "react";
      import {
      Box,
      ThemeProvider,
      createTheme,
      CssBaseline,
      Card,
      CardContent,
      Typography,
      IconButton,
      Stack,
      TextField,
      Button,
      MenuItem,
      CircularProgress,
      Tabs,
      Tab,
      useMediaQuery,
    } from "@mui/material";

      import HomeIcon from "@mui/icons-material/Home";
      import MoreVertIcon from "@mui/icons-material/MoreVert";
      import { DataGrid } from "@mui/x-data-grid";
      import SmartGauge from "../../components/gauges/SmartGauge";
      import { optichiller2CompGaugeConfig } from "../../config/gauges/optichiller2comp";
      import { centrifugaGaugeConfig } from "../../config/gauges/centrifuga";
      import { cotherm2CompGaugeConfig } from "../../config/gauges/cotherm2comp";
      import { cotherm4CompGaugeConfig } from "../../config/gauges/cotherm4comp";
      import { cotherm4Comp2CCGaugeConfig } from "../../config/gauges/cotherm4comp2cc";
      import { ezChiller2CompCT6GaugeConfig } from "../../config/gauges/ezchiller2compct6";
      import { fsc1CompGaugeConfig } from "../../config/gauges/fsc_1comp";
      import { fscUnicoGaugeConfig } from "../../config/gauges/fsc_unico";
      import { fscV21CompGaugeConfig } from "../../config/gauges/fsc_v2_1comp";
      import { fscV3GaugeConfig } from "../../config/gauges/fsc_v3";
      import { fscV31CompGaugeConfig } from "../../config/gauges/fsc_v3_1comp";
      import { licuador1CompGaugeConfig } from "../../config/gauges/licuador1comp";
      import { optichiller2CompCT6GaugeConfig } from "../../config/gauges/optichiller2compct6";
      import { optichiller1CompGaugeConfig } from "../../config/gauges/optichiller1comp";
      import { optichiller1CompCT6GaugeConfig } from "../../config/gauges/optichiller1compct6";
      import { victex1CompGaugeConfig } from "../../config/gauges/victex1comp";

      import {
      optichiller2CompColumns,
      centrifugaColumns,
      cotherm2CompColumns,
      cotherm4CompColumns,
      cotherm4Comp2CCColumns,
      ezChiller2CompCT6Columns,
      fscColumns,
      fsc1CompColumns,
      fscUnicoColumns,
      fscV21CompColumns,
      fscV3Columns,
      fscV31CompColumns,
      licuador1CompColumns,
      optichillerCT6Columns,
      optichiller1CompColumns,
      optichiller1CompCT6Columns,
      victex1CompColumns,
    } from "./config/columns";

    import {
      optichillerSeriesDefs,
      centrifugaSeriesDefs,
      cotherm2CompSeriesDefs,
      cotherm4CompSeriesDefs,
      cotherm4Comp2CCSeriesDefs,
      ezChiller2CompCT6SeriesDefs,
      fscSeriesByView,
      fsc1CompSeriesDefs,
      fscUnicoSeriesDefs,
      fscV21CompSeriesDefs,
      fscV3SeriesByView,
      fscV31CompSeriesDefs,
      licuador1CompSeriesDefs,
      optichillerCT6SeriesDefs,
      optichiller1CompSeriesDefs,
      optichiller1CompCT6SeriesDefs,
      victex1CompSeriesDefs,
    } from "./config/series";

      import { DASH_MAPPERS } from "./config/mappers";
      import { MACHINE_DASH } from "./config/machineDash";
      import { postDashboard } from "./config/api";


      import FscTabs from "../../views/dashboard/FSC_Tabs";
      import AlgorithmsPanel from "./components/AlgorithmsPanel";
      import {
        evaluateOptichiller2CompCondenserFouling,
        evaluateOptichiller2CompCT6CondenserFouling,
        evaluateOptichiller1CompCondenserFouling,
        evaluateOptichiller1CompCT6CondenserFouling,
        evaluateCoTherm2CompCondenserFouling,
        evaluateLicuador1CompCondenserFouling,  
        evaluateVictexCondenserFouling,
        evaluateCentrifugaCondenserFouling,
      } from "./algorithms/condenserFouling";
    import {
      evaluateOptichiller2CompRefrigerantShortage,
      evaluateOptichiller2CompCT6RefrigerantShortage,
      evaluateCentrifugaRefrigerantShortage,
      evaluateOptichiller1CompRefrigerantShortage,
      evaluateOptichiller1CompCT6RefrigerantShortage,
      evaluateCoTherm2CompRefrigerantShortage,
      evaluateEzchiller2CompCT6RefrigerantShortage,
      evaluateFSCRefrigerantShortage,
      evaluateFSC1CompRefrigerantShortage,
      evaluateLicuador1CompRefrigerantShortage,
      evaluateVictexRefrigerantShortage,
      evaluateFSCV31CompRefrigerantShortage,
      evaluateFSCV21CompRefrigerantShortage,
      evaluateFSCUnicoRefrigerantShortage,
    } from "./algorithms/refrigerantShortage";


      import {
        ResponsiveContainer,
        LineChart,
        Line,
        XAxis,
        YAxis,
        CartesianGrid,
        Tooltip,
      } from "recharts";

      const theme = createTheme({
        palette: { mode: "light", background: { default: "#f6f7fb" } },
        shape: { borderRadius: 16 },
        components: {
          MuiCard: {
            styleOverrides: {
              root: {
                boxShadow: "0 2px 8px rgba(0,0,0,.04), 0 12px 24px rgba(0,0,0,.06)",
              },
            },
          },
        },
      });

      // 🔹 Datos de ejemplo para gráfica (demo)
      const tableRows = [
        { id: 1, fecha: "2025-11-04 08:00", valor: 2.1 },
        { id: 2, fecha: "2025-11-04 10:00", valor: 3.5 },
        { id: 3, fecha: "2025-11-04 12:00", valor: 4.2 },
        { id: 4, fecha: "2025-11-04 14:00", valor: 3.8 },
        { id: 5, fecha: "2025-11-04 16:00", valor: 5.0 },
        { id: 6, fecha: "2025-11-04 18:00", valor: 4.1 },
      ];

      // --- Header reutilizable (icono + título con padding) ---
      function PageHeader({ title = "Dashboard", offset = 72 }) {
        return (
          <Box
            sx={{
              mt: {
                xs: `calc(${offset}px + 14px + env(safe-area-inset-top))`,
                sm: `${offset}px`,
              },
              px: { xs: 2, sm: 3, md: 5 },
              pt: { xs: 2, sm: 3 },
              pb: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <HomeIcon sx={{ fontSize: 20, color: "text.secondary", mb: "1px" }} />
            </Box>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                lineHeight: 1,
                color: "text.primary",
                letterSpacing: 0.2,
              }}
            >
              {title}
            </Typography>
          </Box>
        );
      }

      // 🔹 Barra de filtros (misma vista que antes, pero controlada por props)
      function MachineFilterBar({
        idEquipo,
        tipoMaquina,
        onChangeIdEquipo,
        onChangeTipoMaquina,
        ultimoRegistro,
        onBuscar,
        loading,
      }) {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 1.5, sm: 2 },
              flexDirection: { xs: "column", md: "row" },
              px: { xs: 1.5, sm: 2, md: 3 },
              py: { xs: 1.5, sm: 1.5 },
              mt: 1,
              mb: 2,
              mx: "auto",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              width: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" },
              maxWidth: 1650,
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            }}
          >
            {/* IZQUIERDA: filtros */}
            <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: { xs: 1.25, sm: 2 },
                  flexWrap: "wrap",
                  flexDirection: { xs: "column", sm: "row" },
                  width: { xs: "100%", md: "auto" },
                }}
              >
              <TextField
                label="ID Equipo"
                variant="outlined"
                size="small"
                value={idEquipo}
                onChange={(e) => onChangeIdEquipo(e.target.value)}
                sx={{ width: { xs: "100%", sm: 180 } }}
              />

              <TextField
                variant="outlined"
                size="small"
                select
                value={tipoMaquina}
                onChange={(e) => onChangeTipoMaquina(e.target.value)}
                sx={{ width: { xs: "100%", sm: 220 } }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>Tipo de máquina</em>
                </MenuItem>
                <MenuItem value="centrifuga">Centrífuga</MenuItem>
                <MenuItem value="cotherm2comp">CoTherm 2Comp</MenuItem>
                <MenuItem value="cotherm4comp">CoTherm 4Comp</MenuItem>
                <MenuItem value="cotherm4comp2cc">CoTherm 4Comp 2CC</MenuItem>
                <MenuItem value="ezchiller2compct6">ezChiller 2Comp CT6</MenuItem>
                <MenuItem value="fsc">Full Service Cooling</MenuItem>
                <MenuItem value="fsc_1comp">FSC 1Comp</MenuItem>
                <MenuItem value="fsc_unico">FSC UNICO</MenuItem>
                <MenuItem value="fsc_v2_1comp">FSC V2 1Comp</MenuItem>
                <MenuItem value="fsc_v3">FSC V3</MenuItem>
                <MenuItem value="fsc_v3_1comp">FSC V3 1Comp</MenuItem>
                <MenuItem value="licuador1comp">Licuador 1Comp</MenuItem>
                <MenuItem value="optichiller1comp">Optichiller 1Comp</MenuItem>
                <MenuItem value="optichiller1comp_ct6">Optichiller 1Comp CT6</MenuItem>
                <MenuItem value="optichiller2comp">Optichiller 2Comp</MenuItem>
                <MenuItem value="optichiller_ct6">Optichiller 2Comp CT6</MenuItem>
                <MenuItem value="victex1comp">Victex 1 Compresor</MenuItem>
              </TextField>



              <Button
                variant="contained"
                size="small"
                onClick={onBuscar}
                disabled={loading || !idEquipo || !tipoMaquina}
                sx={{
                  bgcolor: "#771726",
                  "&:hover": { bgcolor: "#5a111d" },
                  px: 3,
                  width: { xs: "100%", sm: "auto" },
                  minHeight: { xs: 40, sm: "auto" },
                }}
              >
                {loading ? "Buscando..." : "Buscar"}
              </Button>
            </Box>

            {/* DERECHA: Último registro */}
            <Box
              sx={{
                ml: { xs: 0, md: "auto" },
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Último registro:&nbsp;
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {ultimoRegistro || "—"}
                </Typography>
              </Typography>
            </Box>
          </Box>
        );
      }

      const EVAP_FLOW_RULES_1COMP = {
        100: { correctMin: 4.0, correctMax: 5.5, alarmMin: 7.0 },
        75: { correctMin: 3.0, correctMax: 4.5, alarmMin: 6.0 },
        50: { correctMin: 3.0, correctMax: 4.0, alarmMin: 5.0 },
      };

      const EVAP_FLOW_RULES_2COMP = {
        "100_100": { correctMin: 4.0, correctMax: 5.5, alarmMin: 7.0 },
        "100_75":  { correctMin: 3.5, correctMax: 5.0, alarmMin: 6.5 },
        "100_50":  { correctMin: 3.0, correctMax: 4.5, alarmMin: 6.0 },
        "100_0":   { correctMin: 2.0, correctMax: 2.5, alarmMin: 4.0 },

        "75_75":   { correctMin: 3.0, correctMax: 4.0, alarmMin: 5.5 },
        "75_50":   { correctMin: 2.5, correctMax: 3.5, alarmMin: 5.0 },
        "75_0":    { correctMin: 1.5, correctMax: 2.0, alarmMin: 3.5 },

        "50_50":   { correctMin: 2.0, correctMax: 3.0, alarmMin: 4.5 },
        "50_0":    { correctMin: 1.0, correctMax: 1.5, alarmMin: 3.0 },
      };

      function toAlgorithmNumber(value) {
        if (value == null || value === "") return null;

        const n = Number(String(value).trim().replace(",", "."));
        return Number.isFinite(n) ? n : null;
      }

      function normalizeCompressorLoad(value) {
        const n = toAlgorithmNumber(value);

        if (n == null) return null;

        // Por ahora solo tenemos reglas para 50, 75 y 100.
        // 25% y 0% todavía no se evalúan.
        if (n === 0) return "OFF";
        if (n === 25) return "UNSUPPORTED";

        const validLoads = [50, 75, 100];

        const nearest = validLoads.reduce((prev, curr) =>
          Math.abs(curr - n) < Math.abs(prev - n) ? curr : prev
        );

        return nearest;
      }

      function evaluateEvapFlow1Comp(latestRow) {
        const tempEntrada = toAlgorithmNumber(latestRow?.tempProcesoEntrada);
        const tempSalida = toAlgorithmNumber(latestRow?.tempProcesoSalida);
        const cargaComp1 = normalizeCompressorLoad(latestRow?.porcentajeCargaComp1);

        const deltaT =
          tempEntrada != null && tempSalida != null
            ? tempEntrada - tempSalida
            : null;

        if (tempEntrada == null || tempSalida == null || cargaComp1 == null) {
          return {
            key: "evap_flow",
            title: "Flujo de evaporador",
            status: "nodata",
            message: "No hay suficientes datos para evaluar.",
            cause: "Faltan temperatura de entrada, temperatura de salida o porcentaje de carga.",
            resultLabel: "Resultado",
            deltaT,
            expectedMin: null,
            expectedMax: null,
            alarmMin: null,
            loads: [],
          };
        }

        if (cargaComp1 === "OFF") {
          return {
            key: "evap_flow",
            title: "Flujo de evaporador",
            status: "nodata",
            message: "Compresor sin carga. No se evalúa falta de flujo.",
            cause: "El compresor está apagado o en 0% de carga.",
            resultLabel: "Resultado",
            deltaT,
            expectedMin: null,
            expectedMax: null,
            alarmMin: null,
            loads: [0],
          };
        }

        if (cargaComp1 === "UNSUPPORTED") {
          return {
            key: "evap_flow",
            title: "Flujo de evaporador",
            status: "nodata",
            message: "La carga actual todavía no tiene regla configurada.",
            cause: "Falta definir regla para esta carga de compresor.",
            resultLabel: "Resultado",
            deltaT,
            expectedMin: null,
            expectedMax: null,
            alarmMin: null,
            loads: [latestRow?.porcentajeCargaComp1],
          };
        }

        const rule = EVAP_FLOW_RULES_1COMP[cargaComp1];

        if (!rule) {
          return {
            key: "evap_flow",
            title: "Flujo de evaporador",
            status: "nodata",
            message: "No existe regla para esta carga de compresor.",
            cause: "Falta configurar esta carga en la tabla de reglas.",
            resultLabel: "Resultado",
            deltaT,
            expectedMin: null,
            expectedMax: null,
            alarmMin: null,
            loads: [cargaComp1],
          };
        }

        let status = "ok";
        let message = "Operación dentro de parámetros.";
        let cause = "Sin anomalías detectadas.";
        let resultLabel = "Resultado";

        if (deltaT < rule.correctMin) {
          status = "alarm";
          message = "Posible exceso de flujo en evaporador.";
          cause = "El diferencial de temperatura está por debajo del rango correcto. Posible exceso de flujo de agua o condición de operación fuera de rango.";
          resultLabel = "Posible causa";
        } else if (deltaT >= rule.alarmMin) {
          status = "alarm";
          message = "Posible falta de flujo en evaporador.";
          cause =
            "El diferencial de temperatura está por arriba del límite de alarma. Posible falta de flujo de agua en evaporador.";
          resultLabel = "Posible causa";
        } else if (deltaT > rule.correctMax) {
          status = "warn";
          message = "Diferencial en zona de advertencia.";
          cause =
            "El diferencial está arriba del rango correcto, pero todavía debajo del límite de alarma.";
          resultLabel = "Posible causa";
        }

        return {
          key: "evap_flow",
          title: "Flujo de evaporador",
          status,
          message,
          cause,
          resultLabel,
          deltaT,
          expectedMin: rule.correctMin,
          expectedMax: rule.correctMax,
          alarmMin: rule.alarmMin,
          loads: [cargaComp1],
        };
      }

      function normalizeCompressorLoad2Comp(value) {
      const n = toAlgorithmNumber(value);

      if (n == null) return null;

      if (n <= 0) return 0;

      const validLoads = [50, 75, 100];

      const nearest = validLoads.reduce((prev, curr) =>
        Math.abs(curr - n) < Math.abs(prev - n) ? curr : prev
      );

      return nearest;
    }

    function evaluateEvapFlow2Comp(latestRow) {
      const tempEntrada = toAlgorithmNumber(latestRow?.tempProcesoEntrada);
      const tempSalida = toAlgorithmNumber(latestRow?.tempProcesoSalida);

      const cargaComp1 = normalizeCompressorLoad2Comp(latestRow?.porcentajeCargaComp1);
      const cargaComp2 = normalizeCompressorLoad2Comp(latestRow?.porcentajeCargaComp2);

      const deltaT =
        tempEntrada != null && tempSalida != null
          ? tempEntrada - tempSalida
          : null;

      if (
        tempEntrada == null ||
        tempSalida == null ||
        cargaComp1 == null ||
        cargaComp2 == null
      ) {
        return {
          key: "evap_flow",
          title: "Flujo de evaporador",
          status: "nodata",
          message: "No hay suficientes datos para evaluar.",
          cause: "Faltan temperatura de entrada, temperatura de salida o porcentaje de carga de los compresores.",
          resultLabel: "Resultado",
          deltaT,
          expectedMin: null,
          expectedMax: null,
          alarmMin: null,
          loads: [],
        };
      }

      if (cargaComp1 === 0 && cargaComp2 === 0) {
        return {
          key: "evap_flow",
          title: "Flujo de evaporador",
          status: "nodata",
          message: "Compresores sin carga. No se evalúa flujo de evaporador.",
          cause: "Ambos compresores están apagados o en 0% de carga.",
          resultLabel: "Resultado",
          deltaT,
          expectedMin: null,
          expectedMax: null,
          alarmMin: null,
          loads: [0, 0],
        };
      }

      const sortedLoads = [cargaComp1, cargaComp2].sort((a, b) => b - a);
      const ruleKey = `${sortedLoads[0]}_${sortedLoads[1]}`;
      const rule = EVAP_FLOW_RULES_2COMP[ruleKey];

      if (!rule) {
        return {
          key: "evap_flow",
          title: "Flujo de evaporador",
          status: "nodata",
          message: "No existe regla para esta combinación de carga.",
          cause: `Falta configurar regla para C1 ${cargaComp1}% y C2 ${cargaComp2}%.`,
          resultLabel: "Resultado",
          deltaT,
          expectedMin: null,
          expectedMax: null,
          alarmMin: null,
          loads: [cargaComp1, cargaComp2],
        };
      }

      let status = "ok";
      let message = "Operación dentro de parámetros.";
      let cause = "Sin anomalías detectadas.";
      let resultLabel = "Resultado";

      if (deltaT < rule.correctMin) {
        status = "alarm";
        message = "Posible exceso de flujo en evaporador.";
        cause =
          "El diferencial de temperatura está por debajo del rango correcto. Posible exceso de flujo de agua o condición de operación fuera de rango.";
        resultLabel = "Posible causa";
      } else if (deltaT >= rule.alarmMin) {
        status = "alarm";
        message = "Posible falta de flujo en evaporador.";
        cause =
          "El diferencial de temperatura está por arriba del límite de alarma. Posible falta de flujo de agua en evaporador.";
        resultLabel = "Posible causa";
      } else if (deltaT > rule.correctMax) {
        status = "warn";
        message = "Diferencial en zona de advertencia.";
        cause =
          "El diferencial está arriba del rango correcto, pero todavía debajo del límite de alarma.";
        resultLabel = "Posible causa";
      }

      return {
        key: "evap_flow",
        title: "Flujo de evaporador",
        status,
        message,
        cause,
        resultLabel,
        deltaT,
        expectedMin: rule.correctMin,
        expectedMax: rule.correctMax,
        alarmMin: rule.alarmMin,
        loads: [cargaComp1, cargaComp2],
      };
    }

    function getFixed2CompTotalLoad(cargaComp1, cargaComp2) {
      const comp1 = toAlgorithmNumber(cargaComp1);
      const comp2 = toAlgorithmNumber(cargaComp2);

      if (comp1 == null || comp2 == null) return null;

      const activeComp1 = comp1 > 0 ? 1 : 0;
      const activeComp2 = comp2 > 0 ? 1 : 0;

      return (activeComp1 + activeComp2) * 50;
    }

    function evaluateEvapFlowFscV3Modules(latestRow) {
      const results = [];

      for (let n = 1; n <= 10; n++) {
        const tempEntrada = latestRow?.[`m${n}TempEvapEntrada`];
        const tempSalida = latestRow?.[`m${n}TempEvapSalida`];

        const cargaComp1 = latestRow?.[`m${n}PorcentajeCargaComp1`];
        const cargaComp2 = latestRow?.[`m${n}PorcentajeCargaComp2`];

        const tempEntradaNum = toAlgorithmNumber(tempEntrada);
        const tempSalidaNum = toAlgorithmNumber(tempSalida);

        const cargaTotal = getFixed2CompTotalLoad(cargaComp1, cargaComp2);

        if (
          tempEntradaNum == null ||
          tempSalidaNum == null ||
          cargaTotal == null
        ) {
          continue;
        }

        if (cargaTotal === 0) {
          continue;
        }

        const result = evaluateEvapFlow1Comp({
          tempProcesoEntrada: tempEntradaNum,
          tempProcesoSalida: tempSalidaNum,
          porcentajeCargaComp1: cargaTotal,
        });

        if (result.status === "nodata") {
          continue;
        }

        results.push({
          ...result,
          key: `fsc_v3_m${n}_evap_flow`,
          title: `M${n} Flujo de evaporador`,
          loads: [cargaTotal],
          activeCompressors: cargaTotal / 50,
        });
      }

      if (results.length === 0) {
        return [
          {
            key: "fsc_v3_no_active_modules",
            title: "FSC V3 Flujo de evaporador",
            status: "nodata",
            message: "No hay módulos operando.",
            cause:
              "Todos los módulos tienen carga total 0%. No se evalúa flujo de evaporador hasta que al menos un compresor esté encendido.",
            resultLabel: "Resultado",
            deltaT: null,
            expectedMin: null,
            expectedMax: null,
            alarmMin: null,
            loads: [],
          },
        ];
      }

      return results;
    }

      function evaluateEvapFlowFscV31Comp(latestRow) {
        return evaluateEvapFlow1Comp({
          ...latestRow,
          tempProcesoEntrada: latestRow?.tempEvapEntrada,
          tempProcesoSalida: latestRow?.tempEvapSalida,
        });
      }

      function evaluateEvapFlowFscUnico(latestRow) {
        const cargaTotalFromSp = toAlgorithmNumber(latestRow?.porcentajeCargaTotal);

        const cargaTotal =
          cargaTotalFromSp ??
          getFixed2CompTotalLoad(
            latestRow?.porcentajeCargaComp1,
            latestRow?.porcentajeCargaComp2
          );

        if (cargaTotal == null) {
          return [];
        }

        if (cargaTotal === 0) {
          return [
            {
              key: "fsc_unico_no_active_compressors",
              title: "FSC Único Flujo de evaporador",
              status: "nodata",
              message: "No hay compresores operando.",
              cause:
                "Ambos compresores tienen carga total 0%. No se evalúa flujo de evaporador hasta que al menos un compresor esté encendido.",
              resultLabel: "Resultado",
              deltaT: null,
              expectedMin: null,
              expectedMax: null,
              alarmMin: null,
              loads: [],
            },
          ];
        }

        const result = evaluateEvapFlow1Comp({
          tempProcesoEntrada: latestRow?.tempEvapEnt,
          tempProcesoSalida: latestRow?.tempEvapSal,
          porcentajeCargaComp1: cargaTotal,
        });

        if (result.status === "nodata") {
          return [];
        }

        return [
          {
            ...result,
            key: "fsc_unico_evap_flow",
            title: "FSC Único Flujo de evaporador",
            loads: [cargaTotal],
          },
        ];
      }

      function normalizeCentrifugaSlideValveLoad(value) {
  const n = toAlgorithmNumber(value);

  if (n == null) return null;

  if (n === 0) return "OFF";

  if (n >= 40 && n <= 60) return 50;

  if (n >= 90 && n <= 100) return 100;

  return "UNSUPPORTED";
}

function evaluateEvapFlowCentrifuga(latestRow) {
  const tempEntrada = toAlgorithmNumber(latestRow?.tempProcesoEntrada);
  const tempSalida = toAlgorithmNumber(latestRow?.tempProcesoSalida);
  const slideValve = toAlgorithmNumber(latestRow?.posicionSlideValve);

  const cargaComp1 = normalizeCentrifugaSlideValveLoad(slideValve);

  const deltaT =
    tempEntrada != null && tempSalida != null
      ? tempEntrada - tempSalida
      : null;

  if (tempEntrada == null || tempSalida == null || cargaComp1 == null) {
    return [];
  }

  if (cargaComp1 === "OFF") {
    return [
      {
        key: "centrifuga_no_load",
        title: "Centrífuga Flujo de evaporador",
        status: "nodata",
        message: "Compresor sin carga.",
        cause:
          "La Posición Slide Valve está en 0%. No se evalúa flujo de evaporador.",
        resultLabel: "Resultado",
        deltaT,
        expectedMin: null,
        expectedMax: null,
        alarmMin: null,
        loads: [0],
      },
    ];
  }

  if (cargaComp1 === "UNSUPPORTED") {
    return [
      {
        key: "centrifuga_unsupported_load",
        title: "Centrífuga Flujo de evaporador",
        status: "nodata",
        message: "Posición Slide Valve fuera de rango configurado.",
        cause:
          "Solo se evalúa de 40% a 60% como 50%, y de 90% a 100% como 100%.",
        resultLabel: "Resultado",
        deltaT,
        expectedMin: null,
        expectedMax: null,
        alarmMin: null,
        loads: [slideValve],
      },
    ];
  }

  const result = evaluateEvapFlow1Comp({
    tempProcesoEntrada: tempEntrada,
    tempProcesoSalida: tempSalida,
    porcentajeCargaComp1: cargaComp1,
  });

  if (result.status === "nodata") {
    return [];
  }

  return [
    {
      ...result,
      key: "centrifuga_evap_flow",
      title: "Centrífuga Flujo de evaporador",
      loads: [cargaComp1],
      originalLoad: slideValve,
    },
  ];
}

function evaluateCondenserFlowByRule({
  key,
  title,
  tempTorreEntrada,
  tempTorreSalida,
  rule,
  loads,
  originalLoad,
}) {
  const entrada = toAlgorithmNumber(tempTorreEntrada);
  const salida = toAlgorithmNumber(tempTorreSalida);

  const deltaT =
    entrada != null && salida != null
      ? entrada - salida
      : null;

  if (entrada == null || salida == null || !rule) {
    return [];
  }

  let status = "ok";
  let message = "Flujo de condensador dentro de parámetros.";
  let cause = "Sin anomalías detectadas.";
  let resultLabel = "Resultado";

  if (deltaT < rule.correctMin) {
    status = "alarm";
    message = "Posible exceso de flujo de condensador.";
    cause =
      "El diferencial de temperatura de torre está por debajo del rango correcto. Posible exceso de flujo de agua en condensador o condición de operación fuera de rango.";
    resultLabel = "Posible causa";
  } else if (deltaT >= rule.alarmMin) {
    status = "alarm";
    message = "Posible falta de flujo de condensador.";
    cause =
      "El diferencial de temperatura de torre está por arriba del límite de alarma. Posible bomba apagada, bomba dañada u obstrucción de flujo.";
    resultLabel = "Posible causa";
  } else if (deltaT > rule.correctMax) {
    status = "warn";
   message = "Posible tendencia a bajo flujo de condensador.";
   cause =
    "El diferencial de temperatura de torre está arriba del rango correcto, pero todavía debajo del límite de alarma.";
    resultLabel = "Posible causa";
  } 

  return [
    {
      key,
      title,
      status,
      message,
      cause,
      resultLabel,
      deltaT,
      expectedMin: rule.correctMin,
      expectedMax: rule.correctMax,
      alarmMin: rule.alarmMin,
      loads,
      originalLoad,
    },
  ];
}

function evaluateCondenserFlowCentrifuga(latestRow) {
  const slideValve = toAlgorithmNumber(latestRow?.posicionSlideValve);
  const cargaComp1 = normalizeCentrifugaSlideValveLoad(slideValve);

  if (cargaComp1 === "OFF" || cargaComp1 === "UNSUPPORTED" || cargaComp1 == null) {
    return [
      {
        key: "centrifuga_condenser_flow_no_data",
        title: "Centrífuga Flujo de condensador",
        status: "nodata",
        message: "Posición Slide Valve fuera de rango configurado.",
        cause:
          "Solo se evalúa de 40% a 60% como 50%, y de 90% a 100% como 100%.",
        resultLabel: "Resultado",
        deltaT: null,
        expectedMin: null,
        expectedMax: null,
        alarmMin: null,
        loads: [slideValve],
      },
    ];
  }

  const rule = EVAP_FLOW_RULES_1COMP[cargaComp1];

  return evaluateCondenserFlowByRule({
    key: "centrifuga_condenser_flow",
    title: "Centrífuga Flujo de condensador",
    tempTorreEntrada: latestRow?.tempAguaTorreEntrada,
    tempTorreSalida: latestRow?.tempAguaTorreSalida,
    rule,
    loads: [cargaComp1],
    originalLoad: slideValve,
  });
}

function evaluateCondenserFlow2Comp(
    latestRow,
    title = "Optichiller 2Comp Flujo de condensador",
    key = "optichiller2comp_condenser_flow"
  ) {
    const cargaComp1 = normalizeCompressorLoad2Comp(
      latestRow?.porcentajeCargaComp1
    );

    const cargaComp2 = normalizeCompressorLoad2Comp(
      latestRow?.porcentajeCargaComp2
    );

    if (cargaComp1 == null || cargaComp2 == null) {
      return [];
    }

    if (cargaComp1 === 0 && cargaComp2 === 0) {
      return [];
    }

    const sortedLoads = [cargaComp1, cargaComp2].sort((a, b) => b - a);
    const ruleKey = `${sortedLoads[0]}_${sortedLoads[1]}`;
    const rule = EVAP_FLOW_RULES_2COMP[ruleKey];

    if (!rule) {
      return [];
    }

    return evaluateCondenserFlowByRule({
      key,
      title,
      tempTorreEntrada: latestRow?.tempTorreEntrada,
      tempTorreSalida: latestRow?.tempTorreSalida,
      rule,
      loads: [cargaComp1, cargaComp2],
      originalLoad: [
        latestRow?.porcentajeCargaComp1,
        latestRow?.porcentajeCargaComp2,
      ],
    });
  }

  function evaluateCondenserFlow1Comp(
    latestRow,
    title = "Optichiller 1Comp Flujo de condensador",
    key = "optichiller1comp_condenser_flow"
  ) {
    const cargaComp1 = normalizeCompressorLoad(latestRow?.porcentajeCargaComp1);

    if (cargaComp1 === "OFF" || cargaComp1 === "UNSUPPORTED" || cargaComp1 == null) {
      return [];
    }

    const rule = EVAP_FLOW_RULES_1COMP[cargaComp1];

    if (!rule) {
      return [];
    }

    return evaluateCondenserFlowByRule({
      key,
      title,
      tempTorreEntrada: latestRow?.tempTorreEntrada,
      tempTorreSalida: latestRow?.tempTorreSalida,
      rule,
      loads: [cargaComp1],
      originalLoad: latestRow?.porcentajeCargaComp1,
    });
  }
      
      // ✅ ESTE es el único export default del archivo
      export default function DashboardOnly() {
        const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

        const chartHeight = isMobile ? 380 : 460;
        const chartMinHeight = isMobile ? 260 : 320;
        const chartMargin = isMobile
          ? { top: 8, right: 8, left: -28, bottom: 4 }  
          : { top: 10, right: 24, left: -20, bottom: 16 };

        const chartTickFontSize = isMobile ? 10 : 12;
        const chartMinTickGap = isMobile ? 28 : 60;

        const [isChartTouching, setIsChartTouching] = useState(false);
        const chartTouchTimerRef = React.useRef(null);

        const showChartTooltip = React.useCallback(() => {
          if (chartTouchTimerRef.current) {
            window.clearTimeout(chartTouchTimerRef.current);
          }

          setIsChartTouching(true);
        }, []);

        const hideChartTooltip = React.useCallback(() => {
          if (chartTouchTimerRef.current) {
            window.clearTimeout(chartTouchTimerRef.current);
          }

          chartTouchTimerRef.current = window.setTimeout(() => {
            setIsChartTouching(false);
          }, 80);
        }, []);

        React.useEffect(() => {
          return () => {
            if (chartTouchTimerRef.current) {
              window.clearTimeout(chartTouchTimerRef.current);
            }
          };
        }, []);

        React.useEffect(() => {
          window.requestAnimationFrame(() => {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
          });
        }, []);

        // ✅ Define primero
        const toNum = React.useCallback((v) => {
          if (v == null) return null;
          const s = String(v).trim().replace(",", ".");
          const n = Number(s);
          return Number.isFinite(n) ? n : null;
        }, []);

        // 🔹 filtros
        const [idEquipo, setIdEquipo] = useState("");
        const [tipoMaquina, setTipoMaquina] = useState("");

        // 🔹 tabla
        const [rows, setRows] = useState([]);
        const [loading, setLoading] = useState(false);

        // 🔹 etiqueta “Último registro”
        const [ultimoRegistro, setUltimoRegistro] = useState("");
        
        // 🔹 vista de CoTherm 4Comp: temperaturas / succion / descarga / aceite
        const [cothermView, setCothermView] = useState("temperaturas");

        const [fscView, setFscView] = useState("temperaturas");

        const [fscTab, setFscTab] = useState(0); // 0=MAESTRO, 1..10=MÓDULOS
        const [fscAlgorithmModule, setFscAlgorithmModule] = useState(1);

        React.useEffect(() => {
          setRows([]);
          setUltimoRegistro("");
          setCothermView("temperaturas");
          setFscView("temperaturas");
          setFscTab(0);
          setFscAlgorithmModule(1);
          setLoading(false);
        }, [tipoMaquina]);

        const toGaugeNum = React.useCallback((v) => {
          if (v == null) return null;
          const n = Number(String(v).trim().replace(",", "."));
          if (!Number.isFinite(n)) return null;
          return n;
        }, []);

        const levelToColor = React.useMemo(() => ({
          ok: "green",
          warn: "yellow",
          alarm: "red",
        }), []);

        const normalizeRanges = React.useCallback((cfg) => {
          const list = cfg?.bands ?? cfg?.ranges ?? [];
          if (!Array.isArray(list)) return [];
          return list.map((b) => ({ 
            min: Number(b.from ?? b.min),
            max: Number(b.to ?? b.max),
            color: levelToColor[b.level] ?? b.color ?? "grey",  
          }));
        }, [levelToColor]);

        const columns = React.useMemo(() => {
          const byType = {
            optichiller2comp: optichiller2CompColumns,
            centrifuga: centrifugaColumns,
            cotherm2comp: cotherm2CompColumns,
            cotherm4comp: cotherm4CompColumns,
            cotherm4comp2cc: cotherm4Comp2CCColumns,
            ezchiller2compct6: ezChiller2CompCT6Columns,
            fsc: fscColumns,
            fsc_1comp: fsc1CompColumns, 
            fsc_unico: fscUnicoColumns,
            fsc_v2_1comp: fscV21CompColumns,
            fsc_v3: fscV3Columns,
            fsc_v3_1comp: fscV31CompColumns,
            licuador1comp: licuador1CompColumns,
            optichiller_ct6: optichillerCT6Columns,
            optichiller1comp: optichiller1CompColumns,
            optichiller1comp_ct6: optichiller1CompCT6Columns,
            victex1comp: victex1CompColumns,
          };

          // ✅ SIEMPRE regresa un array
          return Array.isArray(byType[tipoMaquina]) ? byType[tipoMaquina] : [];
        }, [tipoMaquina]);

        React.useEffect(() => {
          if (tipoMaquina === "fsc" || tipoMaquina === "fsc_v3") setFscTab(0);
        }, [tipoMaquina]);


          // ✅ FSC: construir data para FSC_Tabs desde rows[0]
          const fscDashboardData = React.useMemo(() => {
            const latest = rows?.[0] ?? null;
            if (!latest) return { maestro: {}, modulos: [] };

            return {
              maestro: {
                id: latest.numeroEquipo ?? idEquipo ?? "",
                tProcesoEntrada: latest.tempProcesoEntrada ?? null,
                tProcesoSalida: latest.tempProcesoSalida ?? null,
              },
              modulos: Array.from({ length: 10 }, (_, i) => {
                const n = i + 1;
                return {
                  id: latest[`m${n}NumeroEquipo`] ?? "",
                  tEvapSal: latest[`m${n}TempEvapSalida`] ?? null,
                  tDescarga: latest[`m${n}TempDescarga`] ?? null,
                  pSuccion: latest[`m${n}PresSuccion`] ?? null,
                  pDescarga: latest[`m${n}PresDescarga`] ?? null,
                };
              }),
            };
          }, [rows, idEquipo]);

          const fscHeader = React.useMemo(() => {
            const maestroId = fscDashboardData?.maestro?.id ?? "";
            if (fscTab === 0) return { label: "ID Maestro", value: maestroId };

            const idx = fscTab - 1;
            const modId = fscDashboardData?.modulos?.[idx]?.id ?? "";
            return { label: `ID Módulo ${fscTab}`, value: modId || maestroId };
          }, [fscTab, fscDashboardData]);

        React.useEffect(() => {
          if (tipoMaquina === "fsc") setFscView("temperaturas");
        }, [tipoMaquina]);


        const hasData = (rows?.length ?? 0) > 0;

        const chartData = React.useMemo(() => {
        if ((rows?.length ?? 0) === 0) {
          return tableRows.map((r) => ({
            fecha: r.fecha,
            demoValue: r.valor,
          }));
        }

        const sorted = [...rows].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

        if (tipoMaquina === "centrifuga") {
          return sorted.map((row) => ({
            fecha: row.fecha,
            tempProcesoEntrada: toNum(row.tempProcesoEntrada),
            tempProcesoSalida: toNum(row.tempProcesoSalida),
            tempAguaTorreEntrada: toNum(row.tempAguaTorreEntrada),
            tempAguaTorreSalida: toNum(row.tempAguaTorreSalida),
            presAbsolutaSuccion: toNum(row.presAbsolutaSuccion),
            presAbsolutaDescarga: toNum(row.presAbsolutaDescarga),
            tempDescarga: toNum(row.tempDescarga),
            posicionSlideValve: toNum(row.posicionSlideValve),
          }));
        }

        if (tipoMaquina === "cotherm2comp") {
          return sorted.map((row) => ({
            fecha: row.fecha,
            presSuccionComp1: toNum(row.presSuccionComp1),
            presSuccionComp2: toNum(row.presSuccionComp2),
            presionDescargaComp1: toNum(row.presionDescargaComp1),
            presionDescargaComp2: toNum(row.presionDescargaComp2),
            tempProcesoEntrada: toNum(row.tempProcesoEntrada),
            tempProcesoSalida: toNum(row.tempProcesoSalida),
            tempEvapSalidaC1: toNum(row.tempEvapSalidaC1),
            tempEvapSalidaC2: toNum(row.tempEvapSalidaC2),
            tempDescargaComp1: toNum(row.tempDescargaComp1),
            tempDescargaComp2: toNum(row.tempDescargaComp2),

            porcentajeCargaComp1: toNum(row.porcentajeCargaComp1),
            porcentajeCargaComp2: toNum(row.porcentajeCargaComp2),
          }));
        }

        if (tipoMaquina === "cotherm4comp") {
          return sorted.map((row) => ({
            fecha: row.fecha,
            tempHeladaEntrada: toNum(row.tempHeladaEntrada),
            tempHeladaSalida: toNum(row.tempHeladaSalida),
            tempCondEntrada: toNum(row.tempCondEntrada),
            tempCondSalida: toNum(row.tempCondSalida),
            presSuccionComp1: toNum(row.presSuccionComp1),
            presSuccionComp2: toNum(row.presSuccionComp2),
            presSuccionComp3: toNum(row.presSuccionComp3),
            presSuccionComp4: toNum(row.presSuccionComp4),
            presionDescargaComp1: toNum(row.presionDescargaComp1),
            presionDescargaComp2: toNum(row.presionDescargaComp2),
            presionDescargaComp3: toNum(row.presionDescargaComp3),
            presionDescargaComp4: toNum(row.presionDescargaComp4),
            presionDifAceiteComp1: toNum(row.presionDifAceiteComp1),
            presionDifAceiteComp2: toNum(row.presionDifAceiteComp2),
            presionDifAceiteComp3: toNum(row.presionDifAceiteComp3),
            presionDifAceiteComp4: toNum(row.presionDifAceiteComp4),
          }));
        }

        if (tipoMaquina === "cotherm4comp2cc") {
          return sorted.map((row) => ({
            fecha: row.fecha,

            tempHeladaEntrada:    toNum(row.tempHeladaEntrada),
            tempHeladaSalida:     toNum(row.tempHeladaSalida),
            tempCondEntrada:      toNum(row.tempCondEntrada),
            tempCondSalida:       toNum(row.tempCondSalida),
            tempCondSecEntrada:   toNum(row.tempCondSecEntrada),
            tempCondSecSalida:    toNum(row.tempCondSecSalida),

            presSuccionComp1:     toNum(row.presSuccionComp1),
            presSuccionComp2:     toNum(row.presSuccionComp2),
            presSuccionComp3:     toNum(row.presSuccionComp3),
            presSuccionComp4:     toNum(row.presSuccionComp4),

            presionDescargaComp1: toNum(row.presionDescargaComp1),
            presionDescargaComp2: toNum(row.presionDescargaComp2),
            presionDescargaComp3: toNum(row.presionDescargaComp3),
            presionDescargaComp4: toNum(row.presionDescargaComp4),

            presionDifAceiteComp1: toNum(row.presionDifAceiteComp1),
            presionDifAceiteComp2: toNum(row.presionDifAceiteComp2),
            presionDifAceiteComp3: toNum(row.presionDifAceiteComp3),
            presionDifAceiteComp4: toNum(row.presionDifAceiteComp4),
            }));
          }

          if (tipoMaquina === "ezchiller2compct6") {
            return sorted.map((row) => ({
              fecha: row.fecha,

              presionSuccionComp1:    toNum(row.presionSuccionComp1),
              presionSuccionComp2:    toNum(row.presionSuccionComp2),
              presionDescargaComp1:   toNum(row.presionDescargaComp1),
              presionDescargaComp2:   toNum(row.presionDescargaComp2),

              tempProcesoEntrada:     toNum(row.tempProcesoEntrada),
              tempProcesoSalida:      toNum(row.tempProcesoSalida),

              tempRefrCondensado1:    toNum(row.tempRefrCondensado1),
              tempRefrCondensado2:    toNum(row.tempRefrCondensado2),

              tempDescargaComp1:      toNum(row.tempDescargaComp1),
              tempDescargaComp2:      toNum(row.tempDescargaComp2),

              porcentajeCargaComp1: toNum(row.porcentajeCargaComp1),
              porcentajeCargaComp2: toNum(row.porcentajeCargaComp2),
            }));
          }

          if (tipoMaquina === "fsc") {
            return sorted.map((row) => ({
              fecha: row.fecha,

              tempProcesoEntrada: toNum(row.tempProcesoEntrada),
              tempProcesoSalida:  toNum(row.tempProcesoSalida),

              m1TempEvapSalida: toNum(row.m1TempEvapSalida),
              m1TempDescarga:   toNum(row.m1TempDescarga),
              m1PresSuccion:    toNum(row.m1PresSuccion),
              m1PresDescarga:   toNum(row.m1PresDescarga),

              m2TempEvapSalida: toNum(row.m2TempEvapSalida),
              m2TempDescarga:   toNum(row.m2TempDescarga),
              m2PresSuccion:    toNum(row.m2PresSuccion),
              m2PresDescarga:   toNum(row.m2PresDescarga),

              m3TempEvapSalida: toNum(row.m3TempEvapSalida),
              m3TempDescarga:   toNum(row.m3TempDescarga),
              m3PresSuccion:    toNum(row.m3PresSuccion),
              m3PresDescarga:   toNum(row.m3PresDescarga),

              m4TempEvapSalida: toNum(row.m4TempEvapSalida),
              m4TempDescarga:   toNum(row.m4TempDescarga),
              m4PresSuccion:    toNum(row.m4PresSuccion),
              m4PresDescarga:   toNum(row.m4PresDescarga),

              m5TempEvapSalida: toNum(row.m5TempEvapSalida),
              m5TempDescarga:   toNum(row.m5TempDescarga),
              m5PresSuccion:    toNum(row.m5PresSuccion),
              m5PresDescarga:   toNum(row.m5PresDescarga),

              m6TempEvapSalida: toNum(row.m6TempEvapSalida),
              m6TempDescarga:   toNum(row.m6TempDescarga),
              m6PresSuccion:    toNum(row.m6PresSuccion),
              m6PresDescarga:   toNum(row.m6PresDescarga),

              m7TempEvapSalida: toNum(row.m7TempEvapSalida),
              m7TempDescarga:   toNum(row.m7TempDescarga),
              m7PresSuccion:    toNum(row.m7PresSuccion),
              m7PresDescarga:   toNum(row.m7PresDescarga),

              m8TempEvapSalida: toNum(row.m8TempEvapSalida),
              m8TempDescarga:   toNum(row.m8TempDescarga),
              m8PresSuccion:    toNum(row.m8PresSuccion),
              m8PresDescarga:   toNum(row.m8PresDescarga),

              m9TempEvapSalida: toNum(row.m9TempEvapSalida),
              m9TempDescarga:   toNum(row.m9TempDescarga),
              m9PresSuccion:    toNum(row.m9PresSuccion),
              m9PresDescarga:   toNum(row.m9PresDescarga),

              m10TempEvapSalida: toNum(row.m10TempEvapSalida),
              m10TempDescarga:   toNum(row.m10TempDescarga),
              m10PresSuccion:    toNum(row.m10PresSuccion),
              m10PresDescarga:   toNum(row.m10PresDescarga),
            }));
          }

          if (tipoMaquina === "fsc_unico") {
            return sorted.map((row) => ({
              fecha: row.fecha,

              presSuccionC1: toNum(row.presSuccionC1),
              presSuccionC2: toNum(row.presSuccionC2),
              presDescargaC1: toNum(row.presDescargaC1),
              presDescargaC2: toNum(row.presDescargaC2),

              superheatC1: toNum(row.superheatC1),
              superheatC2: toNum(row.superheatC2),

              tempEvapEnt: toNum(row.tempEvapEnt),
              tempEvapSal: toNum(row.tempEvapSal),

              tempSuccionC1: toNum(row.tempSuccionC1),
              tempSuccionC2: toNum(row.tempSuccionC2),

              porcentajeCargaComp1: toNum(row.porcentajeCargaComp1),
              porcentajeCargaComp2: toNum(row.porcentajeCargaComp2),
              porcentajeCargaTotal: toNum(row.porcentajeCargaTotal),
            }));
          }

          if (tipoMaquina === "fsc_v3") {
            return sorted.map((row) => {
              const base = {
                fecha: row.fecha,

                tempProcesoEntrada: toNum(row.tempProcesoEntrada),
                tempProcesoSalida: toNum(row.tempProcesoSalida),
              };

              for (let n = 1; n <= 10; n++) {
                base[`m${n}TempEvapSalida`] = toNum(row[`m${n}TempEvapSalida`]);
                base[`m${n}TempEvapEntrada`] = toNum(row[`m${n}TempEvapEntrada`]);

                base[`m${n}PresDescC1`] = toNum(row[`m${n}PresDescC1`]);
                base[`m${n}PresDescC2`] = toNum(row[`m${n}PresDescC2`]);

                base[`m${n}PorcentajeCargaComp1`] = toNum(
                  row[`m${n}PorcentajeCargaComp1`]
                );

                base[`m${n}PorcentajeCargaComp2`] = toNum(
                  row[`m${n}PorcentajeCargaComp2`]
                );
              }

              return base;
            });
          }

          if (tipoMaquina === "licuador1comp") {
            return sorted.map((row) => ({
              fecha: row.fecha,

              presSuccion: toNum(row.presSuccion),
              presDescarga: toNum(row.presDescarga),

              tempRefrEvap: toNum(row.tempRefrEvap),
              tempProcesoEntrada: toNum(row.tempProcesoEntrada),
              tempProcesoSalida: toNum(row.tempProcesoSalida),

              tempCondEntrada: toNum(row.tempCondEntrada),
              tempCondSalida: toNum(row.tempCondSalida),

              tempDescarga: toNum(row.tempDescarga),

              porcentajeCargaComp1: toNum(row.porcentajeCargaComp1),
            }));
          }

          if (tipoMaquina === "optichiller_ct6") {
            return sorted.map((row) => ({
              fecha: row.fecha,

              presSuccionComp1:    toNum(row.presSuccionComp1),
              presSuccionComp2:    toNum(row.presSuccionComp2),

              presionDescargaComp1: toNum(row.presionDescargaComp1),
              presionDescargaComp2: toNum(row.presionDescargaComp2),

              tempProcesoEntrada:  toNum(row.tempProcesoEntrada),
              tempProcesoSalida:   toNum(row.tempProcesoSalida),

              tempTorreEntrada:    toNum(row.tempTorreEntrada),
              tempTorreSalida:     toNum(row.tempTorreSalida),

              tempDescargaComp1:   toNum(row.tempDescargaComp1),
              tempDescargaComp2:   toNum(row.tempDescargaComp2),

              porcentajeCargaComp1: toNum(row.porcentajeCargaComp1),
              porcentajeCargaComp2: toNum(row.porcentajeCargaComp2),
            }));
          }

          if (tipoMaquina === "optichiller1comp") {
            return sorted.map((row) => ({
              fecha: row.fecha,

              presionSuccionComp1:  toNum(row.presionSuccionComp1),
              presionDescargaComp1: toNum(row.presionDescargaComp1),

              tempProcesoEntrada:   toNum(row.tempProcesoEntrada),
              tempProcesoSalida:    toNum(row.tempProcesoSalida),

              tempTorreEntrada:     toNum(row.tempTorreEntrada),
              tempTorreSalida:      toNum(row.tempTorreSalida),

              tempDescargaComp1:    toNum(row.tempDescargaComp1),

              porcentajeCargaComp1: toNum(row.porcentajeCargaComp1),
            }));
          }

          if (tipoMaquina === "optichiller1comp_ct6") {
            return sorted.map((row) => ({
              fecha: row.fecha,

              presSuccionComp1:   toNum(row.presSuccionComp1),
              presDescargaComp1:  toNum(row.presDescargaComp1),

              tempProcesoEntrada: toNum(row.tempProcesoEntrada),
              tempProcesoSalida:  toNum(row.tempProcesoSalida),

              tempTorreEntrada:   toNum(row.tempTorreEntrada),
              tempTorreSalida:    toNum(row.tempTorreSalida),

              tempDescargaComp1:  toNum(row.tempDescargaComp1),

              porcentajeCargaComp1: toNum(row.porcentajeCargaComp1),
            }));
          }

          if (tipoMaquina === "victex1comp") {
            return sorted.map((row) => ({
              fecha: row.fecha,
              tempProcesoEntrada: toNum(row.tempProcesoEntrada),
              tempProcesoSalida:  toNum(row.tempProcesoSalida),
              tempDescarga:       toNum(row.tempDescarga),
              tempAmbiente:       toNum(row.tempAmbiente),
              presionSuccion:     toNum(row.presionSuccion),
              presionDescarga:    toNum(row.presionDescarga),

              porcentajeCargaComp1: toNum(row.porcentajeCargaComp1),
            }));
          }

          if (tipoMaquina === "fsc_v3_1comp") {
            return sorted.map((row) => ({
              fecha: row.fecha,
              tempEvapSalida: toNum(row.tempEvapSalida),
              tempEvapEntrada: toNum(row.tempEvapEntrada),
              presionSuccionComp1: toNum(row.presionSuccionComp1),
              presionDescargaComp1: toNum(row.presionDescargaComp1),
              sobrecalentamientoComp1: toNum(row.sobrecalentamientoComp1),
              tempSuccionComp1: toNum(row.tempSuccionComp1),

              encendidoComp1: row.encendidoComp1,
              porcentajeCargaComp1: toNum(row.porcentajeCargaComp1),
            }));
          }

          if (tipoMaquina === "fsc_1comp") {
            return sorted.map((row) => ({
              fecha: row.fecha,
              tempEvapSalida: toNum(row.tempEvapSalida),
              tempDescarga: toNum(row.tempDescarga),
              presionSuccion: toNum(row.presionSuccion),
              presionDescarga: toNum(row.presionDescarga),
              sobrecalentamiento: toNum(row.sobrecalentamiento),
              tempSuccion: toNum(row.tempSuccion),
            }));
          }

          if (tipoMaquina === "fsc_v2_1comp") {
            return sorted.map((row) => ({
              fecha: row.fecha,
              tempEvapSalida: toNum(row.tempEvapSalida),
              tempDescarga: toNum(row.tempDescarga),
              presionSuccion: toNum(row.presionSuccion),
              presionDescarga: toNum(row.presionDescarga),
              sobrecalentamiento: toNum(row.sobrecalentamiento),
              tempSuccion: toNum(row.tempSuccion),
            }));
          }



          // 🟢 OPTICHILLER 2COMP
          return sorted.map((row) => ({
            fecha: row.fecha,
            presionSuccionComp1:    toNum(row.presionSuccionComp1),
            presionSuccionComp2:    toNum(row.presionSuccionComp2),
            tempProcesoEntrada:     toNum(row.tempProcesoEntrada),
            tempProcesoSalida:      toNum(row.tempProcesoSalida),
            tempTorreEntrada:       toNum(row.tempTorreEntrada),
            tempTorreSalida:        toNum(row.tempTorreSalida),
            tempDescargaComp1:      toNum(row.tempDescargaComp1),
            tempDescargaComp2:      toNum(row.tempDescargaComp2),
            presionDescargaComp1:   toNum(row.presionDescargaComp1),
            presionDescargaComp2:   toNum(row.presionDescargaComp2),
            porcentajeCargaComp1:  toNum(row.porcentajeCargaComp1),
            porcentajeCargaComp2:  toNum(row.porcentajeCargaComp2),
          }));
        }, [rows, tipoMaquina, toNum]);


      const currentGaugeConfig = React.useMemo(() => {
          if (tipoMaquina === "optichiller2comp") return optichiller2CompGaugeConfig || [];
          if (tipoMaquina === "centrifuga") return centrifugaGaugeConfig || [];
          if (tipoMaquina === "cotherm2comp") return cotherm2CompGaugeConfig || [];
          if (tipoMaquina === "ezchiller2compct6") return ezChiller2CompCT6GaugeConfig || [];
          if (tipoMaquina === "cotherm4comp") return cotherm4CompGaugeConfig?.[cothermView] || [];
          if (tipoMaquina === "cotherm4comp2cc") return cotherm4Comp2CCGaugeConfig?.[cothermView] || [];
          if (tipoMaquina === "fsc_unico") return fscUnicoGaugeConfig || [];

          if (tipoMaquina === "fsc_v3") {
            if (fscTab === 0) return fscV3GaugeConfig.maestro;
            return fscV3GaugeConfig.modulo(fscTab); // módulo seleccionado
          }

          if (tipoMaquina === "licuador1comp") return licuador1CompGaugeConfig || [];
          if (tipoMaquina === "optichiller_ct6") return optichiller2CompCT6GaugeConfig || [];
          if (tipoMaquina === "optichiller1comp") return optichiller1CompGaugeConfig || [];
          if (tipoMaquina === "optichiller1comp_ct6") return optichiller1CompCT6GaugeConfig || [];
          if (tipoMaquina === "victex1comp") return victex1CompGaugeConfig || [];
          if (tipoMaquina === "fsc_v3_1comp") return fscV31CompGaugeConfig || [];
          if (tipoMaquina === "fsc_1comp") return fsc1CompGaugeConfig || [];
          if (tipoMaquina === "fsc_v2_1comp") return fscV21CompGaugeConfig || [];


          return [];
        }, [tipoMaquina, cothermView, fscTab]);

        // 🔹 Series según la máquina actual
        const currentSeriesDefs = React.useMemo(() => {
          if ((rows?.length ?? 0) === 0) return [];

          if (tipoMaquina === "centrifuga") return centrifugaSeriesDefs;
          if (tipoMaquina === "cotherm2comp") return cotherm2CompSeriesDefs;

          if (tipoMaquina === "cotherm4comp") {
            const groupCfg = cotherm4CompGaugeConfig[cothermView] || [];
            const keys = new Set(groupCfg.map(g => g.key));
            return cotherm4CompSeriesDefs.filter(s => keys.has(s.key));
          }

          if (tipoMaquina === "cotherm4comp2cc") {
            return cotherm4Comp2CCSeriesDefs[cothermView] || [];
          }

          if (tipoMaquina === "ezchiller2compct6") return ezChiller2CompCT6SeriesDefs;

          if (tipoMaquina === "fsc") return fscSeriesByView[fscView] || [];

          if (tipoMaquina === "fsc_unico") return fscUnicoSeriesDefs;

          if (tipoMaquina === "fsc_v3") return fscV3SeriesByView[fscView] || [];

          if (tipoMaquina === "licuador1comp") return licuador1CompSeriesDefs;

          if (tipoMaquina === "optichiller_ct6") return optichillerCT6SeriesDefs;

          if (tipoMaquina === "optichiller1comp") return optichiller1CompSeriesDefs;

          if (tipoMaquina === "optichiller1comp_ct6") return optichiller1CompCT6SeriesDefs;

          if (tipoMaquina === "victex1comp") return victex1CompSeriesDefs;

          if (tipoMaquina === "fsc_v3_1comp") return fscV31CompSeriesDefs;

          if (tipoMaquina === "fsc_1comp") return fsc1CompSeriesDefs;

          if (tipoMaquina === "fsc_v2_1comp") return fscV21CompSeriesDefs;

          return optichillerSeriesDefs;
        }, [rows, tipoMaquina, cothermView, fscView]);

        const seriesWithData = React.useMemo(() => {
          if (!currentSeriesDefs?.length) return [];
          return currentSeriesDefs.filter(s => chartData.some(p => p?.[s.key] != null));
        }, [currentSeriesDefs, chartData]);

        React.useEffect(() => {
          if (tipoMaquina === "cotherm4comp" || tipoMaquina === "cotherm4comp2cc") {
            setCothermView("temperaturas");
          }
        }, [tipoMaquina]);


        // helper para formatear fecha local → "YYYY-MM-DDTHH:mm"
        const formatLocal = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          const hours = String(d.getHours()).padStart(2, "0");
          const minutes = String(d.getMinutes()).padStart(2, "0");
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        };  



      const algorithmResults = React.useMemo(() => {
        const latestRow = rows?.[0] ?? null;

        if (!hasData || !latestRow) return [];

      if (
        tipoMaquina === "optichiller2comp" ||
        tipoMaquina === "optichiller_ct6"
      ) {
        const isCt6 =
          tipoMaquina === "optichiller_ct6";

        const condenserFoulingResults =
          tipoMaquina === "optichiller_ct6"
            ? evaluateOptichiller2CompCT6CondenserFouling(rows)
            : evaluateOptichiller2CompCondenserFouling(rows);

        const refrigerantShortageResults = isCt6
          ? evaluateOptichiller2CompCT6RefrigerantShortage(rows)
          : evaluateOptichiller2CompRefrigerantShortage(rows);

        const results = [
          evaluateEvapFlow2Comp(latestRow),

          ...evaluateCondenserFlow2Comp(
            latestRow,
            isCt6
              ? "Optichiller 2Comp CT6 Flujo de condensador"
              : "Optichiller 2Comp Flujo de condensador",
            isCt6
              ? "optichiller_ct6_condenser_flow"
              : "optichiller2comp_condenser_flow"
          ),

          ...condenserFoulingResults,
          ...refrigerantShortageResults,
        ];

        console.log(
          `resultado algoritmo ${tipoMaquina}:`,
          results
        );

        return results;
      }

      if (
        tipoMaquina === "optichiller1comp" ||
        tipoMaquina === "optichiller1comp_ct6"
      ) {
        const isCt6 =
          tipoMaquina === "optichiller1comp_ct6";

        const condenserFoulingResults =
          isCt6
            ? evaluateOptichiller1CompCT6CondenserFouling(
                rows
              )
            : evaluateOptichiller1CompCondenserFouling(
                rows
              );

        const refrigerantShortageResults = isCt6
          ? evaluateOptichiller1CompCT6RefrigerantShortage(rows)
          : evaluateOptichiller1CompRefrigerantShortage(rows);

        const results = [
          evaluateEvapFlow1Comp(latestRow),

          ...evaluateCondenserFlow1Comp(
            latestRow,
            isCt6
              ? "Optichiller 1Comp CT6 Flujo de condensador"
              : "Optichiller 1Comp Flujo de condensador",
            isCt6
              ? "optichiller1comp_ct6_condenser_flow"
              : "optichiller1comp_condenser_flow"
          ),

          ...condenserFoulingResults,
          ...refrigerantShortageResults,
        ];

        console.log(
          `resultado algoritmo ${tipoMaquina}:`,
          results
        );

        return results;
      }

      if (tipoMaquina === "licuador1comp") {

        console.log(
        "DATOS LICUADOR:",
        JSON.stringify(
          {
            fecha: latestRow?.fecha,
            presDescarga: latestRow?.presDescarga,
            tempCondEntrada: latestRow?.tempCondEntrada,
            tempCondSalida: latestRow?.tempCondSalida,
            porcentajeCargaComp1: latestRow?.porcentajeCargaComp1,
            tempProcesoEntrada: latestRow?.tempProcesoEntrada,
            tempProcesoSalida: latestRow?.tempProcesoSalida,
          },
          null,
          2
        )
      );

        const results = [
          evaluateEvapFlow1Comp(latestRow),

          ...evaluateLicuador1CompCondenserFouling(
            rows
          ),

          ...evaluateLicuador1CompRefrigerantShortage(
            rows
          ),
        ];

        console.log(
          `resultado algoritmo ${tipoMaquina}:`,
          results
        );

        return results;
      }

      if (tipoMaquina === "victex1comp") {
        const results = [
          evaluateEvapFlow1Comp(latestRow),

          ...evaluateVictexCondenserFouling(
            rows
          ),

          ...evaluateVictexRefrigerantShortage(
            rows
          ),
        ];

        console.log(
          `resultado algoritmo ${tipoMaquina}:`,
          results
        );

        return results;
      }
        
        if (tipoMaquina === "fsc_v3_1comp") {
          const results = [
            evaluateEvapFlowFscV31Comp(latestRow),

            ...evaluateFSCV31CompRefrigerantShortage(rows),
          ];

          console.log(`resultado algoritmo ${tipoMaquina}:`, results);

          return results;
        }

        if (tipoMaquina === "cotherm2comp") {
          const results = [
            evaluateEvapFlow2Comp(latestRow),

            ...evaluateCoTherm2CompCondenserFouling(
              rows
            ),

            ...evaluateCoTherm2CompRefrigerantShortage(
              rows
            ),
          ];

          console.log(
            `resultado algoritmo ${tipoMaquina}:`,
            results
          );

          return results;
        }

        if (tipoMaquina === "ezchiller2compct6") {
          const results = [
            evaluateEvapFlow2Comp(latestRow),

            ...evaluateEzchiller2CompCT6RefrigerantShortage(
              rows
            ),
          ];

          console.log(
            `resultado algoritmo ${tipoMaquina}:`,
            results
          );

          return results;
        }

        if (tipoMaquina === "fsc_v3") {
          const results = evaluateEvapFlowFscV3Modules(latestRow);
          console.log(`resultado algoritmo ${tipoMaquina}:`, results);
          return results;
        }

        if (tipoMaquina === "fsc_unico") {
          const results = [
            ...evaluateEvapFlowFscUnico(latestRow),
            ...evaluateFSCUnicoRefrigerantShortage(rows),
          ];
          console.log(`resultado algoritmo ${tipoMaquina}:`, results);
          return results;
        }

        if (tipoMaquina === "centrifuga") {

          console.log("CENTRIFUGA RAW PARA FOULING:", {
            fecha: latestRow?.fecha,
            presAbsolutaDescarga:
              latestRow?.presAbsolutaDescarga,
            tempAguaTorreSalida:
              latestRow?.tempAguaTorreSalida,
            posicionSlideValve:
              latestRow?.posicionSlideValve,
          });

          const results = [
            ...evaluateEvapFlowCentrifuga(
              latestRow
            ),

            ...evaluateCondenserFlowCentrifuga(
              latestRow
            ),

            ...evaluateCentrifugaCondenserFouling(
              rows
            ),

            ...evaluateCentrifugaRefrigerantShortage(
              rows
            ),
          ];

          console.log(
            `resultado algoritmo ${tipoMaquina}:`,
            results
          );

          return results;
        }
        
        if (tipoMaquina === "fsc") {
          const results = evaluateFSCRefrigerantShortage(rows);

          console.log(
            `resultado algoritmo ${tipoMaquina}:`,
            results
          );

          return results;
        }

        if (tipoMaquina === "fsc_1comp") {
          const results = evaluateFSC1CompRefrigerantShortage(rows);

          console.log(
            `resultado algoritmo ${tipoMaquina}:`,
            results
          );

          return results;
        }

        if (tipoMaquina === "fsc_v2_1comp") {
          const results = evaluateFSCV21CompRefrigerantShortage(rows);
          console.log(`resultado algoritmo ${tipoMaquina}:`, results);
          return results;
        }

        if (
          tipoMaquina === "cotherm4comp" ||
          tipoMaquina === "cotherm4comp2cc"
        ) {
          return [];
        }

        return [
          {
            key: "evap_flow",
            title: "Flujo de evaporador",
            status: "nodata",
            message: "Algoritmo todavía no configurado para esta máquina.",
            cause: "Pendiente de configuración",
            deltaT: null,
            expectedMax: null,
            alarmMin: null,
            loads: [],
          },
        ];
      }, [hasData, rows, tipoMaquina]);

    const fscAlgorithmOptions = React.useMemo(() => {
    if (tipoMaquina !== "fsc_v3" && tipoMaquina !== "fsc") return [];

    return algorithmResults
      .map((result) => {
        const match =
          result.key.match(/^fsc_v3_m(\d+)_evap_flow$/) ||
          result.key.match(/^fsc_m(\d+)_refrigerant_shortage$/);
        const moduleNumber = match ? Number(match[1]) : null;

        if (!moduleNumber) return null;

        return {
          value: moduleNumber,
          label: `Módulo ${moduleNumber}`,
        };
      })
      .filter(Boolean);
  }, [algorithmResults, tipoMaquina]);

    const selectedFscAlgorithmModule = React.useMemo(() => {
      if (tipoMaquina !== "fsc_v3" && tipoMaquina !== "fsc") return null;

    const exists = fscAlgorithmOptions.some(
      (option) => option.value === fscAlgorithmModule
    );

    return exists
      ? fscAlgorithmModule
      : fscAlgorithmOptions[0]?.value ?? null;
  }, [tipoMaquina, fscAlgorithmOptions, fscAlgorithmModule]);

    const visibleAlgorithmResults = React.useMemo(() => {
    if (tipoMaquina !== "fsc_v3" && tipoMaquina !== "fsc") return algorithmResults;

    if (fscAlgorithmOptions.length === 0) return algorithmResults;

    if (!selectedFscAlgorithmModule) return [];

    return algorithmResults.filter(
      (result) =>
        result.key === `fsc_v3_m${selectedFscAlgorithmModule}_evap_flow` ||
        result.key === `fsc_m${selectedFscAlgorithmModule}_refrigerant_shortage`
    );
  }, [
    algorithmResults,
    tipoMaquina,
    selectedFscAlgorithmModule,
    fscAlgorithmOptions,
  ]);

        const handleBuscar = async () => {
        if (!idEquipo || !tipoMaquina) return;

        const cfg = MACHINE_DASH[tipoMaquina];
        if (!cfg) {
          setUltimoRegistro("Tipo no configurado");
          return;
        }

        setLoading(true);
        setRows([]);

        const ahora = new Date();
        const hace24h = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);
        const fechaInicio = formatLocal(hace24h);
        const fechaFinal = formatLocal(ahora);

        try {
          const data = await postDashboard({
            endpoint: cfg.endpoint,
            idEquipo,
            fechaInicio,
            fechaFinal,
          });

          const mapper = DASH_MAPPERS[tipoMaquina];

          const mappedRaw = mapper ? mapper(data, idEquipo) : [];
          const mapped = Array.isArray(mappedRaw) ? mappedRaw : [];

          setRows(mapped);
          setUltimoRegistro(mapped.length > 0 ? mapped[0].fecha : "Sin datos");


        } catch (err) {
          console.error(err);
          setUltimoRegistro(err?.message || "Error")
        } finally {
          setLoading(false);
        }
      };

        return (
        <ThemeProvider theme={theme}>
          <CssBaseline />

          {/* HEADER como en el demo */}
          <PageHeader title="Dashboard" offset={72} />

          {/* BARRA DE FILTROS con el mismo diseño de antes */}
          <MachineFilterBar
            idEquipo={idEquipo}
            tipoMaquina={tipoMaquina}
            onChangeIdEquipo={setIdEquipo}
            onChangeTipoMaquina={setTipoMaquina}
            ultimoRegistro={ultimoRegistro}
            onBuscar={handleBuscar}
            loading={loading}
          />

          {/* Contenido del dashboard */}
          <Box
            sx={{
              px: { xs: 1.5, sm: 2, md: 3 },
              pb: {
                xs: "calc(104px + env(safe-area-inset-bottom))",
                sm: 3,
              },
            }}
          >

            {/* ===================================================== */}
            {/* 1. GAUGES / MANÓMETROS */}
            {/* ===================================================== */}

            {(tipoMaquina === "cotherm4comp" || tipoMaquina === "cotherm4comp2cc") && hasData && (
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ pb: 2 }}>
                  {/* Header */}
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Vistas CoTherm
                    </Typography>
                  </Stack>

                  {/* Tabs */}
                  <Tabs
                    value={cothermView}
                    onChange={(e, v) => setCothermView(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Temperaturas" value="temperaturas" />
                    <Tab label="Succión" value="succion" />
                    <Tab label="Descarga" value="descarga" />
                    <Tab label="Aceite" value="aceite" />
                  </Tabs>

                  {/* Gauges CoTherm */}
                  <Box
                    sx={{
                      mt: 2,
                      display: "grid",
                      gap: 3,
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                        lg: "repeat(5, 1fr)",
                      },
                    }}
                  >
                    {(() => {
                      const latestRow = rows?.[0] ?? null;
                      const cfgList = currentGaugeConfig || [];

                      return cfgList.map((cfg, i) => {
                        const key = cfg?.key ?? null;
                        const value = key && latestRow ? toGaugeNum(latestRow[key]) : null;
                        const title = cfg?.title ?? cfg?.label ?? `Gauge ${i + 1}`;
                        const unit = cfg?.unit ?? "";
                        const min = cfg?.min ?? 0;
                        const max = cfg?.max ?? 100;
                        const ranges = normalizeRanges(cfg);

                        return (
                          <Box key={`${tipoMaquina}-${key || i}`}>
                            <SmartGauge
                              loading={loading}
                              value={value}
                              title={title}
                              unit={unit}
                              min={min}
                              max={max}
                              ranges={ranges}
                            />
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                </CardContent>
              </Card>
            )}

            {(tipoMaquina === "fsc" || tipoMaquina === "fsc_v3") ? (
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ pt: 2.25 }}>
                  {/* HEADER FSC: título izquierda / ID derecha */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 1.5,
                      flexDirection: { xs: "column", sm: "row" },
                      mb: 1.25,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                      Indicadores (Gauges) - {tipoMaquina === "fsc" ? "FSC" : "FSC V3"}
                    </Typography>

                    <Box sx={{ flex: 1 }} />

                    <Box sx={{ width: { xs: "100%", sm: 320 }, minWidth: { sm: 280 } }}>
                      <TextField
                        label={
                          tipoMaquina === "fsc"
                            ? fscHeader.label
                            : fscTab === 0
                              ? "ID Maestro"
                              : `ID Módulo ${fscTab}`
                        }
                        value={
                          tipoMaquina === "fsc"
                            ? fscHeader.value
                            : fscTab === 0
                              ? rows?.[0]?.numeroEquipo ?? idEquipo ?? ""
                              : rows?.[0]?.[`m${fscTab}NumeroEquipo`] ??
                                rows?.[0]?.numeroEquipo ??
                                idEquipo ??
                                ""
                        }
                        size="small"
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <FscTabs
                      mode={tipoMaquina}
                      data={
                        tipoMaquina === "fsc"
                          ? fscDashboardData
                          : { latestRow: rows?.[0] ?? null }
                      }
                      loading={loading}
                      value={fscTab}
                      onChange={setFscTab}
                      hideIdField
                    />
                  </Box>
                </CardContent>
              </Card>
            ) : tipoMaquina === "cotherm4comp" || tipoMaquina === "cotherm4comp2cc" ? (
              null
            ) : (
              <Box
                sx={{
                  mb: 3,
                  display: "grid",
                  gap: 3,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(5, 1fr)",
                  },
                }}
              >
                {(() => {
                  const latestRow = rows?.[0] ?? null;
                  const cfgList = currentGaugeConfig || [];

                  return cfgList.map((cfg, i) => {
                    const key = cfg?.key ?? null;
                    const value = key && latestRow ? toGaugeNum(latestRow[key]) : null;

                    const title = cfg?.title ?? cfg?.label ?? `Gauge ${i + 1}`;
                    const unit = cfg?.unit ?? "";
                    const min = cfg?.min ?? 0;
                    const max = cfg?.max ?? 100;
                    const ranges = normalizeRanges(cfg);

                    return (
                      <Box key={`${tipoMaquina}-${key || i}`}>
                        <SmartGauge
                          loading={loading}
                          value={value}
                          title={title}
                          unit={unit}
                          min={min}
                          max={max}
                          ranges={ranges}
                        />
                      </Box>
                    );
                  });
                })()}
              </Box>
            )}

            {/* ===================================================== */}
            {/* 2. ALGORITMOS */}
            {/* ===================================================== */}

            {hasData && visibleAlgorithmResults.length > 0 && (
              <AlgorithmsPanel
                results={visibleAlgorithmResults}
                headerExtra={
                  (tipoMaquina === "fsc_v3" || tipoMaquina === "fsc") &&
                  fscAlgorithmOptions.length > 0 ? (
                    <TextField
                      select
                      size="small"
                      label="Módulo diagnóstico"
                      value={selectedFscAlgorithmModule ?? ""}
                      onChange={(e) => setFscAlgorithmModule(Number(e.target.value))}
                      sx={{ minWidth: 180 }}
                    >
                      {fscAlgorithmOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : null
                }
              />
            )}

            {/* ===================================================== */}
            {/* 3. GRÁFICA */}
            {/* ===================================================== */}

            <Card
              sx={{
                height: chartHeight,
                mb: { xs: 2, sm: 3 },
                overflow: "hidden",
              }}
            >
              <CardContent
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  p: { xs: 1.5, sm: 2 },
                  "&:last-child": {
                    pb: { xs: 1.5, sm: 2 },
                  },
                }}
              >
                {/* Header */}
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Últimos datos (Gráfica)
                  </Typography>

                  <Box sx={{ ml: "auto" }}>
                    <IconButton size="small">
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Stack>

                {/* Tabs FSC */}
                {(tipoMaquina === "fsc" || tipoMaquina === "fsc_v3") && (
                  <Box sx={{ mt: -0.5, mb: 1 }}>
                    <Tabs
                      value={fscView}
                      onChange={(e, v) => setFscView(v)}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      <Tab label="Temperaturas" value="temperaturas" />
                      <Tab label="Presiones" value="presiones" />
                    </Tabs>
                  </Box>
                )}

                {/* Contenedor principal de la gráfica */}
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  {/* Leyenda */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: { xs: 0.75, sm: 1.5 },
                      mb: 1,
                      ml: { xs: 0, sm: 4 },
                      pr: { xs: 0, sm: 2 },
                      maxHeight: { xs: 72, sm: 60 },
                      overflowY: "auto",
                    }}
                  >
                    {(rows?.length ?? 0) === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Demo de ejemplo (24 h)
                      </Typography>
                    ) : (
                      seriesWithData.map((s) => (
                        <Box
                          key={s.key}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mr: { xs: 0.75, sm: 2 },
                            maxWidth: { xs: "100%", sm: "auto" },
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              bgcolor: s.color,
                              mr: 0.5,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: { xs: 10.5, sm: 12 },
                              lineHeight: 1.15,
                            }}
                          >
                            {s.label}
                          </Typography>
                        </Box>
                      ))  
                    )}
                  </Box>

                  {/* Gráfica */}
                  <Box
                    onTouchStart={showChartTooltip}
                    onTouchMove={showChartTooltip}
                    onTouchEnd={hideChartTooltip}
                    onTouchCancel={hideChartTooltip}
                    onPointerLeave={hideChartTooltip}
                    sx={{
                      flex: 1,
                      minHeight: chartMinHeight,
                      position: "relative",
                    }}
                  >
                    {loading ? (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(255,255,255,0.6)",
                          zIndex: 2,
                        }}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <CircularProgress size={28} />
                          <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                            Cargando datos...
                          </Typography>
                        </Box>
                      </Box>
                    ) : null}

                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData}
                        margin={chartMargin}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#e0e0e0" />

                        <XAxis
                          dataKey="fecha"
                          tickFormatter={(value) => {
                            if (!value) return "";
                            const s = String(value);
                            return s.length >= 16 ? s.slice(11, 16) : s;
                          }}
                          minTickGap={chartMinTickGap}
                          tickMargin={isMobile ? 4 : 8}
                          tick={{ fontSize: chartTickFontSize, fill: "#666" }}
                          tickLine={false}
                          axisLine={{ stroke: "#bbb" }}
                        />

                        <YAxis
                          tick={{ fontSize: chartTickFontSize, fill: "#666" }}
                          tickLine={false}
                          axisLine={{ stroke: "#bbb" }}
                          domain={["auto", "auto"]}
                          width={isMobile ? 34 : 60}
                        />

                        <Tooltip
                          active={isMobile && !isChartTouching ? false : undefined}
                          wrapperStyle={{ background: "transparent" }}
                          contentStyle={{
                            background: "#222",
                            border: "1px solid #555",
                            color: "#ddd",
                            fontSize: "11px",
                            padding: "6px 8px",
                            lineHeight: "1.2",
                            maxWidth: isMobile ? "240px" : "260px",
                          }}
                          labelStyle={{
                            fontSize: "12px",
                            fontWeight: 600,
                            marginBottom: "4px",
                          }}
                        />

                        {(rows?.length ?? 0) === 0 ? (
                          <Line
                            type="monotone"
                            dataKey="demoValue"
                            name="Demo"
                            stroke="#1976d2"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 5 }}
                            isAnimationActive={false}
                            connectNulls
                          />
                        ) : (
                          seriesWithData.map((s) => (
                            <Line
                              key={s.key}
                              type="monotone"
                              dataKey={s.key}
                              name={s.label}
                              stroke={s.color}
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 5 }}
                              isAnimationActive={false}
                              connectNulls
                            />
                          ))
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* ===================================================== */}
            {/* 4. TABLA */}
            {/* ===================================================== */}

            <Card
              sx={{
                height: { xs: 520, sm: 500 },
                mb: { xs: 2, sm: 0 },
              }}
            >
              <CardContent
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  p: { xs: 1.5, sm: 2 },
                  "&:last-child": {
                    pb: { xs: 1.5, sm: 2 },
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Últimos datos (Tabla)
                  </Typography>
                  <Box sx={{ ml: "auto" }}>
                    <IconButton size="small">
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Stack>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    width: "100%",
                  }}
                >
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableColumnMenu
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25 } },
                      sorting: { sortModel: [{ field: "fecha", sort: "desc" }] },
                    }}
                    sx={{
                      border: "none",

                      "& .MuiDataGrid-virtualScroller": {
                        overflowX: "auto",
                        WebkitOverflowScrolling: "touch",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": {
                          display: "none",
                        },
                      },

                      "& .MuiDataGrid-scrollbar--horizontal": {
                        display: "none",
                      },

                      "& .MuiDataGrid-scrollbarFiller": {
                        display: "none",
                      },

                      "& .MuiDataGrid-columnHeaders": {
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f6f7fb",
                        zIndex: 1,
                      },

                      "& .MuiDataGrid-columnHeader": {
                        display: "flex",
                        justifyContent: "center",
                      },

                      "& .MuiDataGrid-columnHeaderTitle": {
                        textAlign: "center",
                        width: "100%",
                        fontWeight: 600,
                        fontSize: { xs: 12, sm: 13 },
                        color: "#000",
                        whiteSpace: "normal",
                        lineHeight: 1.15,
                      },

                      "& .MuiDataGrid-cell": {
                        fontSize: { xs: 12, sm: 13 },
                        display: "flex",
                        alignItems: "center",
                      },

                      "& .MuiDataGrid-footerContainer": {
                        backgroundColor: "#771726",
                        color: "#ffffff",
                        borderTop: "none",
                        minHeight: { xs: 48, sm: 52 },
                      },

                      "& .MuiTablePagination-root, & .MuiSvgIcon-root": {
                        color: "#ffffff",
                      },

                      "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                        fontSize: { xs: 11, sm: 13 },
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
      </ThemeProvider>
    );
    }